import { useEffect, useState, useMemo } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const ViolencePolicy = () => {
  const navigate = useNavigate();
  const itemId = "violence-policy";
  const fieldOrder = useMemo(() => ["date", "firstName", "lastName", "employeeSignature"], []);
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    }
  }, [fieldOrder]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData, fieldOrder]);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});

    const errors = {};
    if (!formData.date) errors.date = "This field is required";
    else if (!isDateToday(formData.date)) errors.date = "Date must be today";
    if (!String(formData.firstName || "").trim()) errors.firstName = "This field is required";
    if (!String(formData.lastName || "").trim()) errors.lastName = "This field is required";
    if (!formData.employeeSignature) errors.employeeSignature = "Signature is required";

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const accountId = localStorage.getItem("accountId");
    if (!accountId) {
      return navigate("/register");
    }

    // Persist locally for fast reload
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));

    // Derive status from filled fields + validation flags
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const dateValid = isDateToday(formData.date);
    const signatureValid = !!(formData.employeeSignature && String(formData.employeeSignature).length > 0);
    const canBeComplete = allFilled && dateValid && signatureValid;
    const status = allEmpty ? "incomplete" : canBeComplete ? "complete" : "in-progress";
    const progress = status === "complete" ? 100 : status === "in-progress" ? 50 : 0;

    try {
      await axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, {
        status,
        progress,
        formData,
      });
    } catch (saveErr) {
      console.error("Failed to save Violence Policy form to server:", saveErr);
      // keep existing UX: still return to checklist
    }
    if (status === "complete") {
      showSuccess("Form completed successfully!");
    }
    navigate("/register");
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (field === "date") {
        if (value && isDateToday(value)) delete next[field];
      } else {
        if (String(value || "").trim()) delete next[field];
      }
      return next;
    });
  };


  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
          <header className="form-card-header">
            <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
              <h2 className="form-title" style={{ textAlign: "center" }}>Workplace Violence Policy</h2>
            </header>
          </header>

          <section className="form-card-content">
            {err && <div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div>}
            <p className="form-intro">
              Talent Employment Inc. is committed to providing a safe and
              healthy workplace free from actual, attempted, or threatened
              violence in compliance with
              <strong>
                <em> Occupational Health and Safety Act</em>
              </strong>{" "}
              requirements. Management will take all reasonable precautions to
              prevent workplace violence and to protect employees at the
              workplace.
            </p>
            <p className="form-intro paragraph-space">
              Workplace violence is defined under the Occupational Health and
              Safety Act as:
            </p>
            <ul className="form-bullets topbullets">
              <li>
                <strong>a)</strong> the exercise of physical force by a person
                against a worker, in a workplace that causes physical injury to
                the worker
              </li>
              <li>
                <strong>b)</strong> an attempt to exercise physical force
                against a worker, in a workplace, that could cause physical
                injury to the worker
              </li>
              <li>
                <strong>c)</strong> a statement or behavior that is reasonable
                for a worker to interpret as a threat to exercise physical force
                against the worker, in a workplace, that could cause physical
                injury to the worker
              </li>
            </ul>
            <p className="form-intro paragraph-space">
              Violent behavior in the workplace is unacceptable from anyone.
              Workplace violence may arise from a variety of sources including
              customers, contractors, employers, supervisors, workers, and
              members of the public. Employees are required to be familiar with
              and follow the procedures for responding to and reporting on and
              preventing incidents of workplace violence.
            </p>
            <p className="form-intro paragraph-space">
              Any employee who has experienced a violent incident is encouraged
              to report it to Management as soon as possible. No action shall be
              taken against an individual for making a complaint, unless the
              complaint is made maliciously. Any report of violence in the
              workplace will be documented in a Violent Incident Report.
            </p>
            <p className="form-intro paragraph-space">
              Management will take prompt action to investigate the matter. If
              it is determined that further action is merited, the employee who
              exhibited such behavior could be subject to disciplinary action,
              termination of employment and criminal prosecution if necessary.
            </p>
          </section>

          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="firstName">First Name*</label>
              <input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  handleInputChange("firstName", e.target.value)
                }
                required
              />
              {fieldErrors.firstName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.firstName}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="lastName">Last Name*</label>
              <input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                required
              />
              {fieldErrors.lastName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.lastName}</div>}
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
              <button type="button" onClick={handleBack}>Back</button>
              <button type="submit" onClick={handleSubmit}>Submit</button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ViolencePolicy;
